"use client";

import { InputEntryFunctionData } from "@aptos-labs/ts-sdk";
import type { CampaignSummary } from "@decibeltrade/sdk";
import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import type { Reward } from "@/app/(main)/rewards/types";
import { toast } from "@/components/toast";
import { useAnalytics } from "@/contexts/analytics-context";
import { readDexSdk, sdkConfig } from "@/contexts/dex-sdk";
import {
  applyOptimisticClaimBatch,
  type OptimisticClaim,
} from "@/contexts/mutations/campaigns/optimistic-claim";
import { useWalletFeePayerWithFallback } from "@/contexts/mutations/wallet-fee-payer";
import { campaignSummaryQueryKey } from "@/contexts/queries/campaigns/use-campaign-summary";
import { useAuthState } from "@/hooks/use-auth-state";
import type { RewardsClaimEventProps } from "@/lib/analytics";
import { parseClaimCampaignAbort } from "@/utils/move-abort";
import { getServerTime } from "@/utils/server-time";

// Must match MAX_CLAIM_BATCH in move/campaign/sources/campaign_manager.move.
export const MAX_CLAIM_BATCH = 8;

export function chunk<T>(items: readonly T[], size = MAX_CLAIM_BATCH): T[][] {
  if (size <= 0) return [];
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

export class ClaimBatchError extends Error {
  readonly claimedIds: number[];
  readonly errorCode: string | undefined;
  constructor(claimedIds: number[], errorCode: string | undefined, cause: Error) {
    super(cause.message, { cause });
    this.name = "ClaimBatchError";
    this.claimedIds = claimedIds;
    this.errorCode = errorCode;
  }
}

function toEventProps(reward: Reward): RewardsClaimEventProps {
  return {
    source: "claim_all",
    campaignId: reward.id,
    category: reward.category,
    rewardAmountUsd: reward.rewardAmountUsd,
  };
}

/// Chunks input into MAX_CLAIM_BATCH-sized batches, signs one tx per chunk,
/// stops on first rejection. Throws ClaimBatchError carrying claimedIds so
/// onError can route to partial vs total-fail toasts.
export function useClaimAllRewards() {
  const queryClient = useQueryClient();
  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();
  const { accountAddress } = useAuthState();
  const analytics = useAnalytics();
  const t = useTranslations("rewards.toast");

  return useMutation({
    mutationFn: async (rewards: readonly Reward[]): Promise<{ claimedIds: number[] }> => {
      const chunks = chunk(rewards);
      const claimedIds: number[] = [];

      for (const chunkRewards of chunks) {
        for (const reward of chunkRewards) {
          analytics.rewards.claimInitiated(toEventProps(reward));
        }
        try {
          const { ids, txHash } = await submitChunk(chunkRewards, signAndSubmitWalletTransaction);
          applyOptimisticToCache(queryClient, accountAddress, ids, txHash);
          for (const reward of chunkRewards) {
            analytics.rewards.claimSucceeded({ ...toEventProps(reward), txHash });
          }
          claimedIds.push(...ids);
        } catch (err) {
          const cause = err instanceof Error ? err : new Error(String(err));
          const errorCode = parseClaimCampaignAbort(cause) ?? undefined;
          for (const reward of chunkRewards) {
            analytics.rewards.claimFailed({ ...toEventProps(reward), errorCode, error: cause });
          }
          throw new ClaimBatchError(claimedIds, errorCode, cause);
        }
      }

      return { claimedIds };
    },
    onSuccess: ({ claimedIds }) => {
      toast({
        title: t("claimAllSuccessTitle"),
        description: t("claimAllSuccessDescription", { count: claimedIds.length }),
        variant: "success",
      });
    },
    onError: (error, rewards) => {
      if (!(error instanceof ClaimBatchError)) {
        toast({
          title: t("claimAllErrorTitle"),
          description: t("errors.unknown"),
          variant: "error",
        });
        return;
      }
      if (error.claimedIds.length === 0) {
        toast({
          title: t("claimAllErrorTitle"),
          description: error.errorCode ? t(`errors.${error.errorCode}`) : t("errors.unknown"),
          variant: "error",
        });
        return;
      }
      toast({
        title: t("claimAllPartialTitle"),
        description: t("claimAllPartialDescription", {
          claimed: error.claimedIds.length,
          total: rewards.length,
        }),
        variant: "error",
      });
    },
  });
}

type SignAndSubmit = (args: { data: InputEntryFunctionData }) => Promise<{ hash: string }>;

async function submitChunk(
  chunkRewards: readonly Reward[],
  signAndSubmit: SignAndSubmit,
): Promise<{ ids: number[]; txHash: string }> {
  const ids = chunkRewards.map((r) => r.id);
  const data: InputEntryFunctionData = {
    function: `${sdkConfig.deployment.campaignPackage}::campaign_manager::claim_batch_by_id`,
    typeArguments: [],
    functionArguments: [ids],
  };
  const { hash } = await signAndSubmit({ data });
  await readDexSdk.deps.aptos.waitForTransaction({ transactionHash: hash });
  return { ids, txHash: hash };
}

function applyOptimisticToCache(
  queryClient: QueryClient,
  accountAddress: string | undefined,
  campaignIds: number[],
  txHash: string,
) {
  if (!accountAddress) return;
  const nowSec = Math.floor(getServerTime() / 1000);
  const metas: OptimisticClaim[] = campaignIds.map((campaignId) => ({
    campaignId,
    txHash,
    nowSec,
  }));
  queryClient.setQueryData(
    campaignSummaryQueryKey({ accountAddress }),
    (prev: CampaignSummary | undefined) => (prev ? applyOptimisticClaimBatch(prev, metas) : prev),
  );
}
